// tcms-frontend/src/pages/AdminSubscriptionCentre.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { Card } from "react-bootstrap";

import { BASE_URL, security_key as SECURITY_KEY, SERVICE_DEFAULT_PRICES } from "../global";

// --- helper: get the most useful error message from any backend/axios shape ---
function extractApiError(resOrErr, fallback = "Request failed") {
    if (resOrErr && resOrErr.response && resOrErr.response.data) {
        const d = resOrErr.response.data;
        return d?.message || d?.data?.error || (typeof d === "string" ? d : null) || fallback;
    }
    const d = resOrErr?.data;
    if (d) return d.message || d?.data?.error || fallback;
    return resOrErr?.message || fallback;
}

// default price lookup (string because it's bound to <input>)
const defaultPriceFor = (serviceType) => {
    const v = SERVICE_DEFAULT_PRICES?.[serviceType];
    return Number.isFinite(Number(v)) ? String(Number(v)) : "";
};

const labelFor = (s) => (s === "games_enrichment" ? "Games + Enrichment" : s.replace(/_/g, " "));

export default function AdminSubscriptionsCentre() {
    const location = useLocation();
    const navigate = useNavigate();

    // read from router state or sessionStorage (safe refresh)
    const state =
        location.state ||
        (() => {
            try {
                return JSON.parse(sessionStorage.getItem("subsCentreState") || "{}");
            } catch {
                return {};
            }
        })();

    const centKey = state.centKey;
    const centName = state.centName || "Centre";

    useEffect(() => {
        if (location.state) {
            sessionStorage.setItem("subsCentreState", JSON.stringify(location.state));
        } else if (!centKey) {
            navigate("/admin/AdminSubscriptions"); // nothing to show
        }
    }, [location.state, centKey, navigate]);

    const [loading, setLoading] = useState(false);

    // latest subscription (editable dates)
    const [sub, setSub] = useState(null); // { pk_subscription_key, subscription_type, effective_date, end_date }
    const [draft, setDraft] = useState({ effective_date: "", end_date: "" });

    // ALL services (every period)
    const [servicesAll, setServicesAll] = useState([]);
    const [allServices, setAllServices] = useState([]); // allowlist for tabs
    const [activeService, setActiveService] = useState(""); // current tab

    // per-row edit cache + row-level errors (now includes price)
    // { [pk_service_id]: { effective_date, end_date, price } }
    const [serviceEdits, setServiceEdits] = useState({});
    const [rowSavingId, setRowSavingId] = useState(null);
    const [rowDeletingId, setRowDeletingId] = useState(null);
    const [rowErrors, setRowErrors] = useState({}); // { [pk_service_id]: "error text" }

    // add new period for selected service (now includes price)
    const [newPeriod, setNewPeriod] = useState({ effective_date: "", end_date: "", price: "" });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState("");

    // renew form (unchanged; pricing is managed per-service rows)
    const [renewForm, setRenewForm] = useState({
        subscription_type: "annual",
        effective_date: "",
        end_date: "",
        service_type: ["games_enrichment"],
    });

    useEffect(() => {
        if (!centKey) return;
        loadAll();
    }, [centKey]);

    async function loadAll() {
        setLoading(true);
        setCreateError("");
        setRowErrors({});
        try {
            // latest subscription
            const s1 = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getSubscription2",
                fk_cent_key: centKey,
            });
            const subRow = s1?.data?.data || null;
            setSub(subRow);
            setDraft({
                effective_date: subRow?.effective_date || "",
                end_date: subRow?.end_date || "",
            });

            // ALL services (history + current) — includes 'price' column
            const s2 = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getCentreServices",
                fk_cent_key: centKey,
            });
            const allRows = Array.isArray(s2?.data?.data) ? s2.data.data : [];
            setServicesAll(allRows);

            // prime per-row edit cache (include price, fallback to default)
            const cache = {};
            for (const row of allRows) {
                const hasPrice = row.price !== null && row.price !== undefined && row.price !== "";
                cache[row.pk_service_id] = {
                    effective_date: row.effective_date || "",
                    end_date: row.end_date || "",
                    price: hasPrice ? String(Number(row.price)) : defaultPriceFor(row.service_type),
                };
            }
            setServiceEdits(cache);

            // allowlist for tabs
            const s3 = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getAllServices",
            });
            const allowed =
                s3?.data?.data ||
                ["games", "enrichment", "games_enrichment", "parent_app", "video_conferencing"];

            // union of allowed + actually present
            const present = Array.from(new Set(allRows.map((r) => r.service_type))).filter(Boolean);
            const combined = Array.from(new Set([...allowed, ...present]));
            setAllServices(combined);

            // default active tab
            const first = combined[0] || "";
            setActiveService((prev) => prev || first);

            // reset "add new period" inputs (seed default price for the first tab)
            setNewPeriod({ effective_date: "", end_date: "", price: defaultPriceFor(first) });
        } catch (err) {
            toast.error(extractApiError(err, "Failed to load subscription details"));
        } finally {
            setLoading(false);
        }
    }

    // when switching tabs, seed the add-period price from the mapping
    useEffect(() => {
        setNewPeriod((p) => ({ ...p, price: defaultPriceFor(activeService) }));
    }, [activeService]);

    // ==== Latest subscription (update) ====
    async function saveSubscription() {
        if (!sub?.pk_subscription_key) {
            toast.warn("No subscription to update.");
            return;
        }
        if (!draft.effective_date || !draft.end_date) {
            toast.warn("Please fill both dates.");
            return;
        }
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "updateSubscription",
                pk_subscription_key: Number(sub.pk_subscription_key),
                fk_cent_key: centKey,
                effective_date: draft.effective_date,
                end_date: draft.end_date,
                subscription_type: sub.subscription_type || "",
            });
            if (res?.data?.success) {
                toast.success("Subscription updated");
                setSub((prev) =>
                    prev ? { ...prev, effective_date: draft.effective_date, end_date: draft.end_date } : prev
                );
            } else {
                toast.error(extractApiError(res, "Update failed"));
            }
        } catch (err) {
            toast.error(extractApiError(err, "Update failed"));
        }
    }

    // ==== Services by selected type (all periods) ====
    const rowsForActive = useMemo(() => {
        const rows = servicesAll.filter((r) => r.service_type === activeService);
        return rows.sort((a, b) => {
            const ad = a.effective_date || "";
            const bd = b.effective_date || "";
            if (ad > bd) return -1;
            if (ad < bd) return 1;
            return (b.pk_service_id ?? 0) - (a.pk_service_id ?? 0);
        });
    }, [servicesAll, activeService]);

    function statusForRow(row) {
        const today = new Date().toISOString().slice(0, 10);
        if (!row.effective_date || !row.end_date) return "";
        if (today >= row.effective_date && today <= row.end_date) return "Active";
        if (today < row.effective_date) return "Upcoming";
        return "Past";
    }

    function onEditChange(pk, field, val) {
        setServiceEdits((prev) => ({ ...prev, [pk]: { ...(prev[pk] || {}), [field]: val } }));
        setRowErrors((prev) => {
            const next = { ...prev };
            delete next[pk];
            return next;
        });
    }

    async function saveServiceRow(row) {
        const pk = row.pk_service_id;
        const edit = serviceEdits[pk] || {};
        const priceNum = edit.price === "" ? 0 : Number(edit.price);

        if (!edit.effective_date || !edit.end_date) {
            const msg = "Please fill both dates.";
            toast.warn(msg);
            setRowErrors((prev) => ({ ...prev, [pk]: msg }));
            return;
        }
        if (isNaN(priceNum) || priceNum < 0 || !Number.isInteger(priceNum)) {
            const msg = "Price must be a non-negative integer.";
            toast.warn(msg);
            setRowErrors((prev) => ({ ...prev, [pk]: msg }));
            return;
        }

        setRowSavingId(pk);
        setRowErrors((prev) => {
            const n = { ...prev };
            delete n[pk];
            return n;
        });
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "updateCentreServices",
                pk_service_id: Number(pk),
                fk_cent_key: centKey,
                service_type: row.service_type,
                effective_date: edit.effective_date,
                end_date: edit.end_date,
                price: priceNum,
            });
            if (res?.data?.success) {
                toast.success("Service period updated");
                setServicesAll((prev) =>
                    prev.map((r) => (r.pk_service_id === pk ? { ...r, ...edit, price: priceNum } : r))
                );
            } else {
                const msg = extractApiError(res, "Update failed");
                toast.error(msg);
                setRowErrors((prev) => ({ ...prev, [pk]: msg }));
            }
        } catch (err) {
            const msg = extractApiError(err, "Update failed");
            toast.error(msg);
            setRowErrors((prev) => ({ ...prev, [pk]: msg }));
        } finally {
            setRowSavingId(null);
        }
    }

    async function deleteServiceRow(row) {
        if (!window.confirm("Delete this service period?")) return;
        const pk = row.pk_service_id;
        setRowDeletingId(pk);
        setRowErrors((prev) => {
            const n = { ...prev };
            delete n[pk];
            return n;
        });
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "deleteCentreServices",
                pk_service_id: Number(pk),
            });
            if (res?.data?.success) {
                toast.success("Service period deleted");
                setServicesAll((prev) => prev.filter((r) => r.pk_service_id !== pk));
                const edits = { ...serviceEdits };
                delete edits[pk];
                setServiceEdits(edits);
            } else {
                const msg = extractApiError(res, "Delete failed");
                toast.error(msg);
                setRowErrors((prev) => ({ ...prev, [pk]: msg }));
            }
        } catch (err) {
            const msg = extractApiError(err, "Delete failed");
            toast.error(msg);
            setRowErrors((prev) => ({ ...prev, [pk]: msg }));
        } finally {
            setRowDeletingId(null);
        }
    }

    async function createServicePeriod() {
        if (!activeService) {
            const msg = "Select a service first.";
            toast.warn(msg);
            setCreateError(msg);
            return;
        }
        if (!newPeriod.effective_date || !newPeriod.end_date) {
            const msg = "Please fill both dates.";
            toast.warn(msg);
            setCreateError(msg);
            return;
        }
        const priceNum = newPeriod.price === "" ? 0 : Number(newPeriod.price);
        if (isNaN(priceNum) || priceNum < 0 || !Number.isInteger(priceNum)) {
            const msg = "Price must be a non-negative integer.";
            toast.warn(msg);
            setCreateError(msg);
            return;
        }

        setCreating(true);
        setCreateError("");
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "createCentreServices",
                fk_cent_key: centKey,
                service_type: activeService,
                effective_date: newPeriod.effective_date,
                end_date: newPeriod.end_date,
                price: priceNum,
            });

            if (res?.data?.success && (res?.data?.data?.ok || res?.data?.ok)) {
                toast.success("Service period created");
                // after adding, reset price to the default for the current tab
                setNewPeriod({ effective_date: "", end_date: "", price: defaultPriceFor(activeService) });
                await loadAll();
            } else {
                const msg = extractApiError(res, "Create failed");
                toast.error(msg);
                setCreateError(msg);
            }
        } catch (err) {
            const msg = extractApiError(err, "Create failed");
            toast.error(msg);
            setCreateError(msg);
        } finally {
            setCreating(false);
        }
    }

    async function submitRenew() {
        const f = renewForm;
        if (!f.subscription_type || !f.effective_date || !f.end_date || !(f.service_type?.length)) {
            toast.warn("Fill all renewal fields");
            return;
        }
        const type = (f.subscription_type || "").toLowerCase();
        let svc = Array.from(new Set(f.service_type.map((x) => String(x).trim().toLowerCase()).filter(Boolean)));

        if (["trial", "monthly", "annual"].includes(type)) {
            const set = new Set(svc);
            const hasBundle = set.has("games_enrichment");
            const hasPair = set.has("games") && set.has("enrichment");

            // If neither the bundle nor the pair are present, add the bundle by default
            if (!hasBundle && !hasPair) set.add("games_enrichment");
            svc = Array.from(set);
        }

        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "createSubscription",
                fk_cent_key: centKey,
                subscription_type: f.subscription_type,
                effective_date: f.effective_date,
                end_date: f.end_date,
                service_type: svc,
            });
            if (res?.data?.data?.ok || res?.data?.ok) {
                toast.success("Renewal created");
                await loadAll();
            } else {
                toast.error(extractApiError(res, "Renewal failed"));
            }
        } catch (err) {
            toast.error(extractApiError(err, "Renewal failed"));
        }
    }

    return (
        <div className="container">
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="page-title">{centName} — Subscription</h5>
                    <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                        <NavLink to="/calendar">Home</NavLink>
                        <NavLink to="/reports">Reports</NavLink>
                        <NavLink to="/admin/AdminSubscriptions">Subscriptions</NavLink>
                        <Typography color="text.primary">Centre</Typography>
                    </Breadcrumbs>
                </div>
                <div>
                    <button className="btn btn-outline-primary me-2" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                    <button className="btn btn-outline-secondary" onClick={loadAll} disabled={loading}>
                        {loading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Latest subscription */}
            <Card className="card my-card mb-3">
                <div className="card-header">
                    <strong>Latest Add-on Subscription</strong>
                </div>
                <Card.Body>
                    {!sub ? (
                        <em>No add-on subscription found.</em>
                    ) : (
                        <div className="row g-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label">Type</label>
                                <input className="form-control" value={sub.subscription_type || ""} disabled />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Effective</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={draft.effective_date || ""}
                                    onChange={(e) =>
                                        setDraft((prev) => ({ ...prev, effective_date: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">End</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={draft.end_date || ""}
                                    onChange={(e) => setDraft((prev) => ({ ...prev, end_date: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-3">
                                <button className="btn btn-primary" onClick={saveSubscription}>
                                    Update
                                </button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Services — All periods by service type (dropdown) */}
            <Card className="card my-card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Services — All Periods</strong>
                    <div className="d-flex align-items-center gap-2">
                        <label htmlFor="serviceSelect" className="mb-0">Service:</label>
                        <select
                            id="serviceSelect"
                            className="form-select form-select-sm"
                            style={{ minWidth: 240 }}
                            value={activeService || ""}
                            onChange={(e) => setActiveService(e.target.value)}
                            aria-label="Select service"
                        >
                            <option value="">— Select a service —</option>
                            {allServices.map((s) => (
                                <option key={s} value={s}>
                                    {labelFor(s)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <Card.Body>
                    {!activeService ? (
                        <em>No services to display.</em>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table align-middle">
                                    <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th style={{ minWidth: 120 }}>Effective</th>
                                        <th style={{ minWidth: 120 }}>End</th>
                                        <th style={{ minWidth: 120 }}>Price</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rowsForActive.length === 0 && (
                                        <tr>
                                            <td colSpan={5}>
                                                <em>No periods found for this service.</em>
                                            </td>
                                        </tr>
                                    )}
                                    {rowsForActive.map((row) => {
                                        const edit =
                                            serviceEdits[row.pk_service_id] || {
                                                effective_date: row.effective_date || "",
                                                end_date: row.end_date || "",
                                                price:
                                                    row.price !== null &&
                                                    row.price !== undefined &&
                                                    row.price !== ""
                                                        ? String(Number(row.price))
                                                        : defaultPriceFor(row.service_type),
                                            };
                                        const status = statusForRow(row);
                                        const rowErr = rowErrors[row.pk_service_id];
                                        return (
                                            <React.Fragment key={row.pk_service_id}>
                                                <tr>
                                                    <td>
                                                        {status === "Active" && (
                                                            <span className="badge bg-success">Active</span>
                                                        )}
                                                        {status === "Upcoming" && (
                                                            <span className="badge bg-info text-dark">Upcoming</span>
                                                        )}
                                                        {status === "Past" && (
                                                            <span className="badge bg-secondary">Past</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={edit.effective_date}
                                                            onChange={(e) =>
                                                                onEditChange(
                                                                    row.pk_service_id,
                                                                    "effective_date",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={edit.end_date}
                                                            onChange={(e) =>
                                                                onEditChange(
                                                                    row.pk_service_id,
                                                                    "end_date",
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="input-group">
                                                            <span className="input-group-text">$</span>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                className="form-control"
                                                                value={edit.price ?? ""}
                                                                onChange={(e) =>
                                                                    onEditChange(
                                                                        row.pk_service_id,
                                                                        "price",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-primary me-2"
                                                            disabled={rowSavingId === row.pk_service_id}
                                                            onClick={() => saveServiceRow(row)}
                                                        >
                                                            {rowSavingId === row.pk_service_id ? "Saving…" : "Update"}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            disabled={rowDeletingId === row.pk_service_id}
                                                            onClick={() => deleteServiceRow(row)}
                                                        >
                                                            {rowDeletingId === row.pk_service_id
                                                                ? "Deleting…"
                                                                : "Delete"}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {rowErr && (
                                                    <tr>
                                                        <td colSpan={5} className="text-danger small">
                                                            {rowErr}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Add new period */}
                            <div className="border rounded p-3 mt-3">
                                <h6 className="mb-3">Add new period for “{labelFor(activeService)}”</h6>
                                <div className="row g-2 align-items-end">
                                    <div className="col-sm-3">
                                        <label className="form-label">Effective</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={newPeriod.effective_date}
                                            onChange={(e) => {
                                                setNewPeriod((prev) => ({ ...prev, effective_date: e.target.value }));
                                                setCreateError("");
                                            }}
                                        />
                                    </div>
                                    <div className="col-sm-3">
                                        <label className="form-label">End</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={newPeriod.end_date}
                                            onChange={(e) => {
                                                setNewPeriod((prev) => ({ ...prev, end_date: e.target.value }));
                                                setCreateError("");
                                            }}
                                        />
                                    </div>
                                    <div className="col-sm-3">
                                        <label className="form-label">Price</label>
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                className="form-control"
                                                value={newPeriod.price}
                                                onChange={(e) => {
                                                    setNewPeriod((prev) => ({ ...prev, price: e.target.value }));
                                                    setCreateError("");
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-sm-3">
                                        <button
                                            className="btn btn-success mt-4"
                                            disabled={creating}
                                            onClick={createServicePeriod}
                                        >
                                            {creating ? "Creating…" : "Add period"}
                                        </button>
                                    </div>
                                </div>
                                {createError && <div className="text-danger mt-2 small">{createError}</div>}
                                <small className="text-muted d-block mt-2">
                                    Default price is pre-filled from global settings; you can override it here.
                                </small>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Renew subscription (no pricing here) */}
            <Card className="card my-card mb-3">
                <div className="card-header">
                    <strong>Renew Add-on Subscription</strong>
                </div>
                <Card.Body>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Type</label>
                            <select
                                className="form-select"
                                value={renewForm.subscription_type}
                                onChange={(e) =>
                                    setRenewForm((prev) => ({ ...prev, subscription_type: e.target.value }))
                                }
                            >
                                <option value="trial">trial</option>
                                <option value="monthly">monthly</option>
                                <option value="annual">annual</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Effective</label>
                            <input
                                type="date"
                                className="form-control"
                                value={renewForm.effective_date}
                                onChange={(e) => setRenewForm((prev) => ({ ...prev, effective_date: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">End</label>
                            <input
                                type="date"
                                className="form-control"
                                value={renewForm.end_date}
                                onChange={(e) => setRenewForm((prev) => ({ ...prev, end_date: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Services</label>
                            <div className="d-flex flex-wrap gap-3">
                                {allServices.map((s) => (
                                    <label key={s} className="form-check-label">
                                        <input
                                            type="checkbox"
                                            className="form-check-input me-1"
                                            checked={renewForm.service_type.includes(s)}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setRenewForm((prev) => {
                                                    const set = new Set(prev.service_type);
                                                    if (checked) set.add(s);
                                                    else set.delete(s);
                                                    return { ...prev, service_type: Array.from(set) };
                                                });
                                            }}
                                        />
                                        {labelFor(s)}
                                    </label>
                                ))}
                            </div>
                            <small className="text-muted d-block mt-1">
                                For trial/monthly/annual, include either <b>Games + Enrichment</b> (bundle) or both{" "}
                                <b>Games</b> and <b>Enrichment</b>. Extras are allowed.
                            </small>
                        </div>
                    </div>
                    <div className="mt-3">
                        <button className="btn btn-primary me-2" onClick={submitRenew}>
                            Create renewal
                        </button>
                        <button className="btn btn-outline-secondary" onClick={loadAll}>
                            Cancel
                        </button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}
